<script setup lang="ts">
/* eslint-disable @typescript-eslint/no-explicit-any */
interface Props {
  accept?: string;
  uploadUrl?: string;
}

interface UploadResponse {
  success: boolean;
  message?: string;
  rowsRead?: number;
  newCustomers?: number;
  newStaff?: number;
  newOrders?: number;
  errors?: Array<{ rowNumber: number; error: string }>;
}

const props = withDefaults(defineProps<Props>(), {
  accept: ".csv",
  uploadUrl: "/api/import-csv"
});

const emit = defineEmits<{
  "file-selected": [file: File];
  "upload-started": [file: File];
  "upload-succeeded": [response: UploadResponse];
  "upload-failed": [error: unknown];
}>();

const { showError } = useErrorModal();

// State management
const selectedFile = ref<File | null>(null);
const isUploading = ref(false);
const isCompleted = ref(false);
const fileInput = ref<HTMLInputElement | null>(null);

// Result state
const uploadResult = ref<{
  status: "success" | "error" | null;
  message: string;
  details?: UploadResponse;
}>({
  status: null,
  message: "",
  details: undefined
});

// Computed
const hasFile = computed(() => !!selectedFile.value);
const iconName = computed(() => {
  if (isUploading.value) return "i-lucide-loader-2";
  if (uploadResult.value.status === "error") return "i-heroicons-exclamation-triangle-20-solid";
  if (isCompleted.value) return "i-heroicons-check-circle-20-solid";
  return "i-heroicons-check-circle-20-solid";
});
const iconClass = computed(() => ({
  "w-4 h-4": true,
  "animate-spin": isUploading.value,
  "text-success": isCompleted.value && uploadResult.value.status === "success",
  "text-error": uploadResult.value.status === "error"
}));

// Methods
function openFileDialog() {
  fileInput.value?.click();
}

function onFileSelected(event: Event) {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];

  if (file) {
    selectedFile.value = file;
    isUploading.value = true;
    uploadResult.value = { status: null, message: "", details: undefined };

    emit("file-selected", file);
    startUpload(file);
  }
}

async function startUpload(file: File) {
  console.log("Starting upload for:", file.name);
  emit("upload-started", file);

  try {
    // Send file directly to backend API (backend will handle R2 upload)
    const formData = new FormData();
    formData.append("csvFile", file);

    console.log("📤 Uploading to backend API...");

    const response = await $fetch<UploadResponse>(props.uploadUrl, {
      method: "POST",
      body: formData
    });

    console.log("✅ Import completed:", response);
    isUploading.value = false;
    isCompleted.value = true;

    // Show success result
    uploadResult.value = {
      status: "success",
      message: "File imported successfully!",
      details: response
    };

    emit("upload-succeeded", response);

    // Reset file input
    if (fileInput.value) {
      fileInput.value.value = "";
    }
  } catch (error: any) {
    console.error("Upload failed:", error);
    isUploading.value = false;
    isCompleted.value = false;

    // Show error modal
    showError({
      title: "Upload Failed!",
      message: error?.message || error?.data?.message || "An unknown error occurred while uploading the file."
    });

    // Update state
    uploadResult.value = {
      status: "error",
      message: error?.message || error?.data?.message || "An unknown error occurred.",
      details: undefined
    };

    emit("upload-failed", error);

    // Reset file input
    if (fileInput.value) {
      fileInput.value.value = "";
    }
  }
}
</script>

<template>
  <div class="space-y-3">
    <h3 class="text-sm font-medium">Upload CSV File</h3>

    <p class="text-xs">
      Select your latest CSV file. This operation will preserve all your current
      data and only add new records.
    </p>

    <!-- Hidden file input -->
    <input
      ref="fileInput"
      type="file"
      :accept="props.accept"
      class="hidden"
      @change="onFileSelected"
    />

    <!-- Upload button -->
    <UButton
      label="Select File"
      color="primary"
      variant="subtle"
      size="sm"
      block
      @click="openFileDialog"
    />

    <!-- File info section -->
    <UCard v-if="hasFile" :ui="{ body: 'p-0 sm:p-2' }">
      <div class="flex items-center gap-2 m-0 p-0">
        <UBadge
          color="neutral"
          class="light:text-neutral-450 dark:text-neutral-300"
          :label="selectedFile!.name.split('.').pop()?.toUpperCase() || 'FILE'"
          variant="outline"
          size="xs"
        />

        <span class="text-xs truncate flex-1">
          {{ selectedFile!.name }}
        </span>

        <span class="text-xs font-medium">
          {{ readableFileSize(selectedFile!.size) }}
        </span>

        <UIcon
          :name="iconName"
          :class="iconClass"
        />
      </div>
    </UCard>

    <div v-if="isUploading" class="text-xs font-light text-center text-muted">
      Please wait while processing your file...
    </div>

    <!-- Success Result -->
    <UCard
      v-if="isCompleted && uploadResult.status === 'success' && uploadResult.details"
      :ui="{ body: 'p-5' }"
    >
      <div class="space-y-4">
        <!-- Header -->
        <div class="flex items-center gap-2.5">
          <UIcon
            name="i-lucide-check-circle-2"
            class="w-5 h-5 text-success"
          />
          <div>
            <h4 class="text-sm font-semibold">Import Successful</h4>
            <p class="text-xs text-muted">Data has been processed and saved</p>
          </div>
        </div>

        <!-- Divider -->
        <div class="border-t" />

        <!-- Stats list -->
        <div class="space-y-3">
          <div class="flex items-center justify-between py-2">
            <span class="text-sm text-muted">Rows Read</span>
            <span
              class="text-xl font-semibold"
              :class="{ 'text-muted': !uploadResult.details.rowsRead }"
            >
              {{ uploadResult.details.rowsRead ? uploadResult.details.rowsRead.toLocaleString() : '-' }}
            </span>
          </div>

          <div class="flex items-center justify-between py-2 border-t border-dashed">
            <span class="text-sm text-muted">New Orders</span>
            <span
              class="text-xl font-semibold"
              :class="{ 'text-muted': !uploadResult.details.newOrders }"
            >
              {{ uploadResult.details.newOrders ? uploadResult.details.newOrders.toLocaleString() : '-' }}
            </span>
          </div>

          <div class="flex items-center justify-between py-2 border-t border-dashed">
            <span class="text-sm text-muted">New Customers</span>
            <span
              class="text-xl font-semibold"
              :class="{ 'text-muted': !uploadResult.details.newCustomers }"
            >
              {{ uploadResult.details.newCustomers ? uploadResult.details.newCustomers.toLocaleString() : '-' }}
            </span>
          </div>

          <div class="flex items-center justify-between py-2 border-t border-dashed">
            <span class="text-sm text-muted">New Staff</span>
            <span
              class="text-xl font-semibold"
              :class="{ 'text-muted': !uploadResult.details.newStaff }"
            >
              {{ uploadResult.details.newStaff ? uploadResult.details.newStaff.toLocaleString() : '-' }}
            </span>
          </div>

          <!-- Error count (if any) -->
          <div
            v-if="uploadResult.details.errors && uploadResult.details.errors.length > 0"
            class="flex items-center justify-between py-2 border-t border-dashed"
          >
            <span class="text-sm text-warning">Rows with Errors</span>
            <span class="text-xl font-semibold text-warning">
              {{ uploadResult.details.errors.length.toLocaleString() }}
            </span>
          </div>
        </div>
      </div>
    </UCard>
  </div>
</template>
